import { Box, Button, Menu, MenuItem } from '@mui/material'
import { FC, useContext, useEffect, useState } from 'react'
import { KeyboardArrowDown } from '@mui/icons-material'
import {
  BillingType,
  BillStatus,
  FetchPublicBillDetailsQuery,
  PaymentMethod,
  useFetchRemainingFlatFeeLazyQuery
} from '../../../../generated'
import HostedPaymentForm from '../../payments/HostedPaymentForm'
import { AppContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import config from '../../../../config'
import { useReactToPrint } from 'react-to-print'
import { Alert } from '../../common/Alert'
import { FINAL_BILL_STATUSES } from '../../../constants'

interface BillViewSidebarProps {
  token: string
  amount: number
  bill: FetchPublicBillDetailsQuery['fetchPublicBillDetails']['bill'] | undefined
  refetch: () => void
}

const BillViewSidebar: FC<BillViewSidebarProps> = ({ amount, token, refetch, bill }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [remainingFlatFee, setRemainingFlatFee] = useState(0)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>()
  const [isPrinting, setIsPrinting] = useState(false)
  const { isOpen, dispatch: appDispatch } = useContext(AppContext)

  const [fetchRemainingFlatFee] = useFetchRemainingFlatFeeLazyQuery({
    onCompleted: data => {
      if (data.fetchRemainingFlatFee.remainingFlatFee) {
        setRemainingFlatFee(data.fetchRemainingFlatFee.remainingFlatFee)
      }
    }
  })

  const billingType = bill?.case?.billing?.billingType
  const caseId = bill?.case?.id
  const status = bill?.status
  const payableBillStatuses: BillStatus[] = FINAL_BILL_STATUSES.filter(
    status => status !== BillStatus.Paid && status !== BillStatus.PrePaid
  )

  const open = Boolean(anchorEl)
  const canPayBill = amount > 0 && status && payableBillStatuses.includes(status)

  const handlePayBillClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handlePaymentOption = (option: PaymentMethod) => {
    setPaymentMethod(option)
    appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: true })
    handleClose()
  }

  const handleDownloadPDF = useReactToPrint({
    content: () => document.getElementById('bill-preview'),
    documentTitle: `Bill_${token}`,
    onBeforePrint: () => {
      setIsPrinting(true)
    },
    onAfterPrint: () => {
      if (isPrinting) {
        Alert.success('Bill downloaded successfully')
        setIsPrinting(false)
      }
    },
    onPrintError: () => {
      setIsPrinting(false)
      Alert.error('Failed to download bill')
    }
  })

  useEffect(() => {
    if (caseId && billingType === BillingType.FlatFee) {
      fetchRemainingFlatFee({
        variables: {
          fetchRemainingFlatFeeInput: { caseId }
        }
      })
    }
  }, [caseId, billingType, fetchRemainingFlatFee])

  return (
    <Box sx={{ maxWidth: { xs: '100%', md: 350 } }}>
      {/* COMMENTED TILL AUTHORIZED NOT SETUP */}
      {/* <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handlePayBillClick}
        endIcon={<KeyboardArrowDown />}
        disabled={!canPayBill}
      >
        PAY BILL
      </Button> */}

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handlePaymentOption(PaymentMethod.Card)}>Via Debit / Credit Card</MenuItem>
        <MenuItem onClick={() => handlePaymentOption(PaymentMethod.Ach)}>Via ACH</MenuItem>
        <MenuItem onClick={() => handlePaymentOption(PaymentMethod.ApplePay)} disabled>
          Via Apple Pay
        </MenuItem>
      </Menu>

      <Button variant="outlined" fullWidth onClick={handleDownloadPDF} sx={{ mt: 3 }}>
        DOWNLOAD CLIENT RECEIPT
      </Button>

      {isOpen && paymentMethod && billingType && (
        <HostedPaymentForm
          clientKey={config.authorizeNet.operating.clientKey || ''}
          apiLoginID={config.authorizeNet.operating.apiLoginID || ''}
          billAmount={amount}
          totalPendingAmount={remainingFlatFee}
          paymentMethod={paymentMethod}
          billAccessToken={token}
          refetch={refetch}
          billingType={billingType}
        />
      )}
    </Box>
  )
}

export default BillViewSidebar
